import i18n from 'i18next';

import { findPreferredLanguage } from "i18n";
import {
    CLIENT_SETTINGS_PREFIX,
    ClientSettingMetaInfo,
    HomeNotConnectedError,
    Node,
    NodeApiError,
    NodeName
} from "api";
import { Storage } from "storage";
import { ClientAction } from "state/action";
import { WithContext } from "state/action-types";
import { dispatch, select } from "state/store-sagas";
import { homeIntroduced } from "state/init-barriers";
import { isConnectedToHome } from "state/home/selectors";
import {
    SettingsClientValuesLoadAction,
    settingsClientValuesLoaded,
    SettingsClientValuesLoadedAction,
    settingsClientValuesLoadFailed,
    SettingsDeleteNodeRequestCancelAction,
    SettingsDeleteNodeRequestLoadAction,
    settingsDeleteNodeRequestLoaded,
    settingsDeleteNodeRequestLoadFailed,
    SettingsDeleteNodeRequestSendAction,
    settingsDeleteNodeRequestStatusSet,
    settingsDeleteNodeRequestUpdateFailed,
    SettingsGrantsDeleteAction,
    settingsGrantsDeleted,
    SettingsGrantsDialogConfirmAction,
    settingsGrantsDialogConfirmed,
    settingsGrantsDialogConfirmFailed,
    SettingsGrantsLoadAction,
    settingsGrantsLoaded,
    settingsGrantsLoadFailed,
    settingsLanguageChanged,
    SettingsMnemonicLoadAction,
    SettingsNodeMetaLoadAction,
    settingsNodeMetaLoaded,
    settingsNodeMetaLoadFailed,
    SettingsNodeValuesLoadAction,
    settingsNodeValuesLoaded,
    SettingsNodeValuesLoadedAction,
    settingsNodeValuesLoadFailed,
    SettingsTokensCreateAction,
    settingsTokensCreated,
    settingsTokensCreateFailed,
    SettingsTokensDeleteAction,
    settingsTokensDeleted,
    SettingsTokensLoadAction,
    settingsTokensLoaded,
    settingsTokensLoadFailed,
    SettingsTokensNewTokenCopyAction,
    SettingsTokensUpdateAction,
    settingsTokensUpdated,
    settingsTokensUpdateFailed,
    settingsUpdate,
    SettingsUpdateAction,
    settingsUpdateFailed,
    settingsUpdateSucceeded,
    SettingsUpdateSucceededAction
} from "state/settings/actions";
import { errorThrown } from "state/error/actions";
import { saga } from "state/saga";
import {
    getSetting,
    getSettingNode,
    getSettingsClient,
    getSettingsClientMeta,
    isSettingsClientValuesLoaded,
    isSettingsNodeLoaded
} from "state/settings/selectors";
import { mnemonicSet } from "state/nodename/actions";
import { flashBox } from "state/flashbox/actions";
import { messageBox } from "state/messagebox/actions";
import * as Browser from "ui/browser";
import { REL_HOME } from "util/rel-node-name";
import { clipboardCopy } from "util/clipboard";

export default [
    saga("SETTINGS_NODE_VALUES_LOAD", "", settingsNodeValuesLoadSaga),
    saga("SETTINGS_NODE_VALUES_LOADED", "", settingsNodeValuesLoadedSaga),
    saga("SETTINGS_NODE_META_LOAD", "", settingsNodeMetaLoadSaga),
    saga("SETTINGS_CLIENT_VALUES_LOAD", "", settingsClientValuesLoadSaga),
    saga("SETTINGS_CLIENT_VALUES_LOADED", "", settingsClientValuesLoadedSaga),
    saga("SETTINGS_UPDATE", null, settingsUpdateSaga),
    saga("SETTINGS_UPDATE_SUCCEEDED", null, settingsUpdateSucceededSaga),
    saga("SETTINGS_MNEMONIC_LOAD", "", settingsMnemonicLoadSaga),
    saga("SETTINGS_GRANTS_LOAD", "", settingsGrantsLoadSaga),
    saga("SETTINGS_GRANTS_DIALOG_CONFIRM", payload => payload.nodeName, settingsGrantsDialogConfirmSaga),
    saga("SETTINGS_GRANTS_DELETE", payload => payload.nodeName, settingsGrantsDeleteSaga),
    saga("SETTINGS_TOKENS_LOAD", "", settingsTokensLoadSaga),
    saga("SETTINGS_TOKENS_CREATE", null, settingsTokensCreateSaga),
    saga("SETTINGS_TOKENS_UPDATE", payload => payload.id, settingsTokensUpdateSaga),
    saga("SETTINGS_TOKENS_DELETE", payload => payload.id, settingsTokensDeleteSaga),
    saga("SETTINGS_TOKENS_NEW_TOKEN_COPY", null, settingsTokensNewTokenCopySaga),
    saga("SETTINGS_DELETE_NODE_REQUEST_LOAD", "", settingsDeleteNodeRequestLoadSaga),
    saga("SETTINGS_DELETE_NODE_REQUEST_SEND", "", settingsDeleteNodeRequestSendSaga),
    saga("SETTINGS_DELETE_NODE_REQUEST_CANCEL", "", settingsDeleteNodeRequestCancelSaga)
];

async function settingsNodeValuesLoadSaga(action: WithContext<SettingsNodeValuesLoadAction>): Promise<void> {
    await homeIntroduced();
    try {
        const settings = await Node.getNodeSettings(action, REL_HOME);
        dispatch(settingsNodeValuesLoaded(settings).causedBy(action));
    } catch (e) {
        dispatch(settingsNodeValuesLoadFailed().causedBy(action));
        dispatch(errorThrown(e));
    }
}

async function settingsNodeMetaLoadSaga(action: WithContext<SettingsNodeMetaLoadAction>): Promise<void> {
    await homeIntroduced();
    try {
        const metadata = await Node.getNodeSettingsMetadata(action, REL_HOME);
        dispatch(settingsNodeMetaLoaded(metadata).causedBy(action));
    } catch (e) {
        dispatch(settingsNodeMetaLoadFailed().causedBy(action));
        dispatch(errorThrown(e));
    }
}

function isMobileSetting(meta: Map<string, ClientSettingMetaInfo>, name: string): boolean {
    return meta.get(name)?.scope === "mobile";
}

function isDeviceSetting(meta: Map<string, ClientSettingMetaInfo>, name: string): boolean {
    return meta.get(name)?.scope === "device";
}

function storeSettings(): void {
    Storage.storeSettings(select(getSettingsClient));
}

async function settingsClientValuesLoadSaga(action: WithContext<SettingsClientValuesLoadAction>): Promise<void> {
    await homeIntroduced();
    try {
        let settings = await Node.getClientSettings(action, REL_HOME, CLIENT_SETTINGS_PREFIX);
        if (window.Android) {
            const mobileData = window.Android.loadSettings();
            if (mobileData != null) {
                const mobileSettings = JSON.parse(mobileData)
                    .map((t: {name: string, value: string}) =>
                        ({name: CLIENT_SETTINGS_PREFIX + t.name, value: t.value}));
                settings = settings.concat(mobileSettings);
            }
        }
        const clientMeta = select(getSettingsClientMeta);
        settings = settings.filter(t => !isDeviceSetting(clientMeta, t.name));
        dispatch(settingsClientValuesLoaded(settings, false).causedBy(action));
    } catch (e) {
        if (e instanceof HomeNotConnectedError) {
            dispatch(settingsClientValuesLoaded([], false).causedBy(action));
        } else {
            dispatch(settingsClientValuesLoadFailed().causedBy(action));
            dispatch(errorThrown(e));
        }
    }
}

async function updateLanguage(caller: ClientAction): Promise<void> {
    let lang = select(state => getSetting(state, "language") as string);
    if (lang != null) {
        if (lang === "auto") {
            lang = findPreferredLanguage();
        }
        if (lang !== i18n.language) {
            await Browser.changeLanguage(lang);
            dispatch(settingsLanguageChanged().causedBy(caller));
        }
    }
    updateEmailLanguage(caller);
}

async function settingsClientValuesLoadedSaga(action: SettingsClientValuesLoadedAction): Promise<void> {
    await updateLanguage(action);
    if (!action.payload.stored) {
        storeSettings();
    }
}

function settingsNodeValuesLoadedSaga(action: SettingsNodeValuesLoadedAction): void {
    updateEmailLanguage(action);
}

function updateEmailLanguage(caller: ClientAction): void {
    const clientSettingsLoaded = select(isSettingsClientValuesLoaded);
    const nodeSettingsLoaded = select(isSettingsNodeLoaded);

    if (!clientSettingsLoaded || !nodeSettingsLoaded) {
        return;
    }

    const lang = select(state => getSettingNode(state, "email.language") as string);
    if (lang !== i18n.language) {
        dispatch(settingsUpdate([{name: "email.language", value: i18n.language}]).causedBy(caller));
    }
}

async function settingsUpdateSaga(action: WithContext<SettingsUpdateAction>): Promise<void> {
    const {settings, onSuccess} = action.payload;

    const clientMeta = select(getSettingsClientMeta);
    const toHome = settings
        .filter(t => !isMobileSetting(clientMeta, t.name) && !isDeviceSetting(clientMeta, t.name));
    const toMobile = settings
        .filter(t => isMobileSetting(clientMeta, t.name))
        .map(t => ({name: t.name.substring(CLIENT_SETTINGS_PREFIX.length), value: t.value}));
    const connectedToHome = select(isConnectedToHome);
    try {
        if (connectedToHome) {
            await Node.updateSettings(action, REL_HOME, toHome);
        }
        if (window.Android && toMobile.length > 0) {
            window.Android.storeSettings(JSON.stringify(toMobile));
        }
        dispatch(settingsUpdateSucceeded(settings, onSuccess).causedBy(action));
        storeSettings();
    } catch (e) {
        dispatch(settingsUpdateFailed().causedBy(action));
        dispatch(errorThrown(e));
    }
}

async function settingsUpdateSucceededSaga(action: SettingsUpdateSucceededAction): Promise<void> {
    await updateLanguage(action);

    if (action.payload.onSuccess != null) {
        action.payload.onSuccess();
    }
}

async function settingsMnemonicLoadSaga(action: WithContext<SettingsMnemonicLoadAction>): Promise<void> {
    await homeIntroduced();
    try {
        const keyMnemonic = await Node.getStoredMnemonic(action, REL_HOME);
        if (action.context.homeOwnerName != null) {
            const name = NodeName.shorten(action.context.homeOwnerName);
            dispatch(mnemonicSet(name, keyMnemonic.mnemonic).causedBy(action));
        }
    } catch (e) {
        dispatch(errorThrown(e));
    }
}

async function settingsGrantsLoadSaga(action: WithContext<SettingsGrantsLoadAction>): Promise<void> {
    await homeIntroduced();
    try {
        const grants = await Node.getAllGrants(action, REL_HOME);
        dispatch(settingsGrantsLoaded(grants).causedBy(action));
    } catch (e) {
        dispatch(settingsGrantsLoadFailed().causedBy(action));
        dispatch(errorThrown(e));
    }
}

async function settingsGrantsDialogConfirmSaga(action: WithContext<SettingsGrantsDialogConfirmAction>): Promise<void> {
    const {nodeName, revoke} = action.payload;

    try {
        const info = await Node.grantOrRevoke(action, REL_HOME, nodeName, {scope: revoke, revoke: true});
        dispatch(settingsGrantsDialogConfirmed(nodeName, info.scope).causedBy(action));
    } catch (e) {
        dispatch(settingsGrantsDialogConfirmFailed().causedBy(action));
        dispatch(errorThrown(e));
    }
}

async function settingsGrantsDeleteSaga(action: WithContext<SettingsGrantsDeleteAction>): Promise<void> {
    const {nodeName} = action.payload;

    try {
        await Node.revokeAll(action, REL_HOME, nodeName);
        dispatch(settingsGrantsDeleted(nodeName).causedBy(action));
    } catch (e) {
        dispatch(errorThrown(e));
    }
}

async function settingsTokensLoadSaga(action: WithContext<SettingsTokensLoadAction>): Promise<void> {
    await homeIntroduced();
    try {
        const tokens = await Node.getTokens(action, REL_HOME);
        dispatch(settingsTokensLoaded(tokens).causedBy(action));
    } catch (e) {
        dispatch(settingsTokensLoadFailed().causedBy(action));
        dispatch(errorThrown(e));
    }
}

async function settingsTokensCreateSaga(action: WithContext<SettingsTokensCreateAction>): Promise<void> {
    const {password, name, permissions, onLoginIncorrect} = action.payload;

    try {
        const token = await Node.createToken(
            action, REL_HOME, {login: "admin", password, name, permissions},
            ["credentials.login-incorrect", "credentials.not-created"]
        );
        dispatch(settingsTokensCreated(token).causedBy(action));
    } catch (e) {
        if (e instanceof NodeApiError && e.errorCode === "credentials.login-incorrect" && onLoginIncorrect != null) {
            onLoginIncorrect();
        } else {
            dispatch(errorThrown(e));
        }
        dispatch(settingsTokensCreateFailed().causedBy(action));
    }
}

async function settingsTokensUpdateSaga(action: WithContext<SettingsTokensUpdateAction>): Promise<void> {
    const {id, name, permissions} = action.payload;

    try {
        const token = await Node.updateToken(action, REL_HOME, id, {name, permissions});
        dispatch(settingsTokensUpdated(token).causedBy(action));
    } catch (e) {
        dispatch(settingsTokensUpdateFailed().causedBy(action));
        dispatch(errorThrown(e));
    }
}

async function settingsTokensDeleteSaga(action: WithContext<SettingsTokensDeleteAction>): Promise<void> {
    const {id} = action.payload;

    try {
        await Node.deleteToken(action, REL_HOME, id);
        dispatch(settingsTokensDeleted(id).causedBy(action));
    } catch (e) {
        dispatch(errorThrown(e));
    }
}

async function settingsTokensNewTokenCopySaga(action: SettingsTokensNewTokenCopyAction): Promise<void> {
    const token = select().settings.tokens.dialog.newToken;
    if (token == null) {
        return;
    }
    await clipboardCopy(token.token);
    if (!Browser.isAndroidBrowser()) {
        dispatch(flashBox(i18n.t("token-copied")).causedBy(action));
    }
}

async function settingsDeleteNodeRequestLoadSaga(
    action: WithContext<SettingsDeleteNodeRequestLoadAction>
): Promise<void> {
    try {
        const status = await Node.getDeleteNodeRequestStatus(action, REL_HOME);
        dispatch(settingsDeleteNodeRequestLoaded(status.requested).causedBy(action));
    } catch (e) {
        dispatch(settingsDeleteNodeRequestLoadFailed().causedBy(action));
        dispatch(errorThrown(e));
    }
}

async function settingsDeleteNodeRequestSendSaga(
    action: WithContext<SettingsDeleteNodeRequestSendAction>
): Promise<void> {
    try {
        const status = await Node.sendDeleteNodeRequest(
            action, REL_HOME, {message: action.payload.message}, ["delete-node.no-email"]
        );
        dispatch(settingsDeleteNodeRequestStatusSet(status.requested).causedBy(action));
    } catch (e) {
        dispatch(settingsDeleteNodeRequestUpdateFailed().causedBy(action));
        if (e instanceof NodeApiError) {
            dispatch(messageBox(i18n.t("set-email-provider-contact")));
        } else {
            dispatch(errorThrown(e));
        }
    }
}

async function settingsDeleteNodeRequestCancelSaga(
    action: WithContext<SettingsDeleteNodeRequestCancelAction>
): Promise<void> {
    try {
        const status = await Node.cancelDeleteNodeRequest(action, REL_HOME);
        dispatch(settingsDeleteNodeRequestStatusSet(status.requested).causedBy(action));
    } catch (e) {
        dispatch(settingsDeleteNodeRequestUpdateFailed().causedBy(action));
        dispatch(errorThrown(e));
    }
}
